import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'

interface CameraPromptProps {
  onStart: () => void | Promise<void>
}

export const CameraPrompt: React.FC<CameraPromptProps> = ({ onStart }) => {
  const { t } = useTranslation()
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionAttempts, setConnectionAttempts] = useState(0)

  // Reset connection state when component mounts
  useEffect(() => {
    setIsConnecting(false)
    setConnectionAttempts(0)
  }, [])

  const handleClick = async (): Promise<void> => {
    if (isConnecting) {
      return // Prevent multiple simultaneous connection attempts
    }

    setIsConnecting(true)
    setConnectionAttempts((prev) => prev + 1)

    try {
      await onStart()
      // If onStart succeeds, it will handle closing the prompt
      // Don't reset state here as the component will unmount
    } catch (error) {
      console.error('CameraPrompt: Connection failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      cogoToast.error(t('camera.prompt.connectionFailed', `Connection failed: ${errorMessage}`), {
        hideAfter: 5
      })
      // Reset connecting state to allow retry
      setIsConnecting(false)
      // Don't close the prompt - let user retry
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 cursor-pointer"
      onClick={handleClick}
    >
      <div className="text-center text-white">
        <h2 className="text-3xl font-bold mb-4">
          {isConnecting
            ? t('camera.prompt.connecting', 'Connecting...')
            : t('camera.prompt.title', 'Click to Start Camera')}
        </h2>
        <p className="text-lg opacity-80">
          {isConnecting
            ? t('camera.prompt.pleaseWait', 'Please wait...')
            : t('camera.prompt.instruction', 'Click anywhere to connect your camera')}
        </p>
        {connectionAttempts > 1 && !isConnecting && (
          <p className="text-sm opacity-60 mt-2">
            {t('camera.prompt.retryHint', 'Click again to retry')}
          </p>
        )}
      </div>
    </div>
  )
}
